
import { extname } from 'path';

import { IFile, ITemplate } from ".";
import { getExtension, MimeType } from "./mimeTypes";

function bufferToBase64(buffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
function stringToBuffer(str: string) {
  const buf = new ArrayBuffer(str.length);
  const array = new Int8Array(buf);
  for (let i = 0, strLen = str.length; i < strLen; i++) {
    array[i] = str.charCodeAt(i);
  }
  return buf;
}
function base64ToBuffer(str: string) {
  return stringToBuffer(atob(str));
}

function jsonTemplateToIMainTemplate(template: any): IMainTemplate | null {
  if (!template) return null;
  template.filename = template.filename || template.name || undefined;
  if (template.template_type === 'converter') {
    return template;
  }
  template.file = template.file || (template.html_template_content && btoa(template.html_template_content)) || undefined;
  template.template_type = template.template_type || template.filename.replace(/(.*\.)*/g, '');
  return template;
}

function jsonTemplateFromITemplate(template: ITemplate) {
  const res = {
    name: calculateFilename(template.filename, template.mimeType),
    mime_type: template.mimeType,
  } as any;
  if ('buffer' in template) {
    res.file_source = 'base64';
    res.file_content = bufferToBase64(template.buffer);
  } else if ('url' in template) {
    res.file_source = 'url';
    res.file_url = template.url;
  } else {
    res.ERROR = `No idea how to convert ITemplate to JSON`;
    console.error('No idea how to convert this ITemplate to JSON:', template);
  }
  return res;
}
function jsonTemplateToITemplate(template: any): ITemplate | null {
  if (!template) return null;
  const filename = template.filename || template.name || undefined;
  let mimeType = template.mime_type;
  if (!mimeType && filename) {
    const ext = extname(filename).substr(1);
    mimeType = ext && MimeType[ext] || '';
  }
  if (!mimeType) {
    console.error(`Missing mimeType for '${filename}'`);
    return null;
  }
  let file_content = template.file_content;
  switch (template.file_source) {
    case 'base64':
      file_content = file_content && atob(template.file_content);
    case 'plain':
      if (!file_content) {
        console.error(`Missing file_content for '${filename}' with file_source '${template.file_source}'`);
        return null;
      }
      const buffer = stringToBuffer(file_content);
      return { filename, mimeType, buffer };
    case 'url':
      const url: string = template.file_url;
      if (!url) {
        console.error(`Missing url for '${filename}' with file_source '${template.file_source}'`);
        return null;
      }
      return { filename, mimeType, url};
    default:
      console.error(`Unknown file_source '${template.file_source}' for '${filename}'`);
      return null;
  }
}

function calculateFilename(filename: string, mimeType?: MimeType) {
  const ext = extname(filename).substr(1);
  if (!mimeType && ext) mimeType = MimeType[ext] as MimeType;
  if (!mimeType) return filename;
  return ext ? filename.substring(0, filename.length - ext.length - 1) : filename;
}

const CALCULATED_DEFAULT = {
  aop_remote_debug: 'No',
  apex_version: 'web editor 2',
  api_key: '1C511A58ECC73874E0530100007FD01A',
  version: '3.0',
}

export interface IMainTemplate {
  template_type: string;
  filename: string;
  file?: string;
}

export class JsonComposer {
  public prepend: ITemplate[] = [];
  public append: ITemplate[] = [];
  public templates: ITemplate[] = [];
  public template: IMainTemplate | null;
  public files: IFile[] = [{ name: 'file1', data: {} }];
  public calculated: any = {};
  public json = JSON.stringify(this.calculated);

  private _outputType: string = 'pdf';
  private listeners: Array<(calculated: any) => void> = [];

  public onChange(listener: (calculated: any) => void) {
    this.listeners.push(listener);
  }
  public clear(keepData = false) {
    this.prepend = [];
    this.append = [];
    this.templates = [];
    this.template = null;
    if (!keepData) this.files = [{ name: 'file1', data: {} }];
    this.calculated = {};
    return this.calculate();
  }
  public calculate(): any {
    const prepend_files = this.prepend.map(jsonTemplateFromITemplate);
    const append_files = this.append.map(jsonTemplateFromITemplate);
    const templates = this.templates.map(jsonTemplateFromITemplate);
    this.calculated = {
      ...CALCULATED_DEFAULT,
      ...this.calculated,
      tool: 'webeditor-new',
      prepend_files,
      append_files,
      template: this.template,
      output: {
        ...(this.calculated.output || {}),
        output_type: this._outputType,
        output_encoding: 'raw',
      },
      files: this.files,
      templates,
    };
    this.json = JSON.stringify(this.calculated);
    this.listeners.forEach(l => l(this.calculated));
    return this.calculated;
  }
  public set(calculated: any) {
    this.calculated = calculated;
    this.files = calculated.files || this.files || [];
    this.template = jsonTemplateToIMainTemplate(calculated.template);
    this.prepend = (calculated.prepend_files || []).map(jsonTemplateToITemplate);
    this.append = (calculated.append_files || []).map(jsonTemplateToITemplate);
    this.templates = (calculated.templates || []).map(jsonTemplateToITemplate);
    this.json = JSON.stringify(calculated);
    this._outputType = calculated.output && calculated.output.output_type;
    this.calculate();
  }
  public setMainTemplate(template?: ITemplate | null) {
    if (template && !getExtension(template.mimeType)) {
      console.error(`Invalid template! Can't turn mimeType '${template.mimeType}' into an extension. Setting template to null!`);
      template = null;
    }
    this.template = template ? {
      filename: calculateFilename(template.filename, template.mimeType),
      file: ('buffer' in template) ? bufferToBase64(template.buffer) : undefined,
      template_type: getExtension(template.mimeType)!,
    } : null;
  }
  public getMainTemplate(): ITemplate | null {
    if (!this.template) return null;
    const { template_type, filename, file } = this.template;
    return {
      filename,
      mimeType: MimeType[template_type] || 'application/unknown',
      buffer: file ? base64ToBuffer(file) : new ArrayBuffer(0),
    };
  }
  public get outputType(): string {
    return this._outputType;
  }
  public set outputType(outputType: string) {
    this._outputType = outputType;
    this.calculate();
  }
}

export const MainComposer = new JsonComposer();



// WEBPACK FOOTER //
// ./src/logic/composer.ts